// ============================================================================
// MileageCard — "record my mileage" from Ask AI (item #1011), built like the
// other part cards to Ben's approved card demo (docs/build-room/demos/
// item-944.html). What it shows is mileageCard(); the save is commitMileage().
// ============================================================================

import { useState } from 'react';
import { Car } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { CopilotProposalBase } from '../../../shared/types';
import { mileageCard, vehicleOf, type MileageDraft } from '../mileage-actions';
import { CardShell } from './card-shell';
import { FieldControl } from './building-action-card';

interface Props {
  proposal: CopilotProposalBase;
  isCommitting: boolean;
  onDoIt: (edited_input: Record<string, unknown>, was_edited: boolean) => void;
  onDiscard: () => void;
}

export function MileageCard({ proposal, isCommitting, onDoIt, onDiscard }: Props) {
  const draft = proposal.input as unknown as MileageDraft;
  const [odometer, setOdometer] = useState<unknown>(draft.odometer);
  const card = mileageCard(draft, odometer);
  const wasEdited = Number(odometer) !== Number(draft.odometer);

  return (
    <CardShell
      title={`${card.title} · ${vehicleOf(draft)}`}
      icon={<Car className="h-4 w-4" />}
      accentClass="border-tcs-border"
      status={proposal.status}
      committedLabel="Saved"
      isCommitting={isCommitting}
      doItDisabled={!!card.missing}
      doItDisabledReason={card.missing ?? undefined}
      doItLabel={card.doItLabel}
      onDoIt={() => onDoIt({ ...draft, odometer: Number(odometer) }, wasEdited)}
      onDiscard={onDiscard}
      after={
        <Link to={`/fleet/vehicle/${encodeURIComponent(draft.vehicle_id)}`} className="text-sm font-medium text-tcs-text underline">
          Open the vehicle
        </Link>
      }
    >
      {card.info.map((i) => (
        <div key={i.label} className="flex min-h-[44px] items-center justify-between gap-3 rounded-md border border-tcs-border px-2 py-1.5 text-sm">
          <span className="text-xs text-tcs-text-muted">{i.label}</span>
          <span className="text-right text-tcs-text">{i.value}</span>
        </div>
      ))}
      <FieldControl spec={{ key: 'odometer', label: 'Odometer (miles)', kind: 'number', required: true }} value={odometer} onChange={setOdometer} />
    </CardShell>
  );
}
